import React from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "./lib/notify";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { t, useLang } from "./lib/i18n";
import { finishAppGoogleLogin, isNativeApp } from "./lib/googleLogin";
import { useAdTop } from "./lib/ads";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import Lobby from "./pages/Lobby";
import Room from "./pages/Room";
import Game from "./pages/Game";
import JoinRoom from "./pages/JoinRoom";
import { DebugWinnerTrigger } from "./components/debug/DebugWinnerTrigger";
import "./App.css";

// A rematch goes from one game straight to another: remount so nothing of
// the finished game (staging, state version) leaks into the new one.
function GameRoute() {
  const { roomId } = useParams();
  return <Game key={roomId} />;
}

// The privacy policy is a static page (public/privacy.html). A browser that holds
// an old copy of the app for /privacy lands here: send it to the page itself.
function PrivacyRedirect() {
  React.useEffect(() => { window.location.replace("/privacy.html"); }, []);
  return null;
}

// The apps: Google sign-in comes back as the link com.ramitounsia.app://auth/callback (lib/googleLogin.js),
// while the app runs or as the link that launched it.
function AppLinks() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!isNativeApp()) return undefined;
    let cancelled = false;
    let handle = null;
    const onLink = async (url) => {
      const result = await finishAppGoogleLogin(url);
      if (!result || cancelled) return;
      if (result.user) {
        setUser(result.user);
        navigate(result.redirectTo || "/lobby", { replace: true });
      } else if (result.error !== "access_denied") {
        toast.error(t("common.googleFailed"));
      }
    };
    import("@capacitor/app")
      .then(({ App: CapApp }) => {
        if (cancelled) return;
        handle = CapApp.addListener("appUrlOpen", ({ url }) => onLink(url));
        return CapApp.getLaunchUrl().then((launch) => onLink(launch?.url));
      })
      .catch((e) => console.warn("[links]", e));
    return () => {
      cancelled = true;
      handle?.then((h) => h.remove()).catch(() => {});
    };
  }, [setUser, navigate]);
  return null;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/lobby" element={<Lobby />} />
      <Route path="/join/:roomId" element={<JoinRoom />} />
      <Route path="/room/:roomId" element={<Room />} />
      <Route path="/game/:roomId" element={<GameRoute />} />
      <Route path="/privacy" element={<PrivacyRedirect />} />
    </Routes>
  );
}

function App() {
  const { dir } = useLang();
  // The AdMob banner covers the top of the Lobby and the game in the apps: toasts come under it.
  const adTop = useAdTop();
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <AppLinks />
          <DebugWinnerTrigger />
          <Toaster
            position="top-right"
            dir={dir}
            offset={{ top: 46 + adTop, right: 8 }}
            mobileOffset={{ top: 40 + adTop }}
            toastOptions={{
              classNames: {
                toast: "bg-slate-900 border-white/10 text-white",
                title: "text-white",
                description: "text-slate-300",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
